import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { FiCopy, FiPlus, FiTrash2, FiUpload, FiX } from 'react-icons/fi';
import { MdDownload } from 'react-icons/md';
import type { TeamMember } from '@/lib/sdk/team/types';
import type { UserBotTaskInput } from '@/lib/sdk/adhoc/types';

const uuidv4 = () => crypto.randomUUID();

// ─── Types ────────────────────────────────────────────────────────────────────

interface EditorRow {
  _localId: string;
  task_name: string;
  description: string;
  extra: Record<string, string>; // extra cols between description and due_date
  due_date: string;
  assigned_to: string;
  group: string;
  _errors?: Record<string, string>;
}

interface FillState {
  srcRowIdx: number;
  col: string;
  value: string;
  endRow: number;
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

type Action =
  | { type: 'ADD_ROWS'; count: number }
  | { type: 'SET_CELL'; localId: string; col: string; value: string }
  | { type: 'DELETE_ROW'; localId: string }
  | { type: 'DUPLICATE_ROW'; localId: string }
  | { type: 'SET_ROW_ERRORS'; localId: string; errors: Record<string, string> }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'RESET' }
  | { type: 'DELETE_EXTRA_COL'; colKey: string }
  | { type: 'LOAD_ROWS'; rows: UserBotTaskInput[] };

function blankRow(): EditorRow {
  return { _localId: uuidv4(), task_name: '', description: '', extra: {}, due_date: '', assigned_to: '', group: '' };
}

function reducer(state: EditorRow[], action: Action): EditorRow[] {
  switch (action.type) {
    case 'ADD_ROWS':
      return [...state, ...Array.from({ length: action.count }, blankRow)];
    case 'SET_CELL':
      return state.map((r) => {
        if (r._localId !== action.localId) return r;
        if (action.col === 'task_name') return { ...r, task_name: action.value, _errors: { ...r._errors, task_name: '' } };
        if (action.col === 'description') return { ...r, description: action.value };
        if (action.col === 'due_date') return { ...r, due_date: action.value, _errors: { ...r._errors, due_date: '' } };
        if (action.col === 'assigned_to') return { ...r, assigned_to: action.value, _errors: { ...r._errors, assigned_to: '' } };
        if (action.col === 'group') return { ...r, group: action.value };
        return { ...r, extra: { ...r.extra, [action.col]: action.value } };
      });
    case 'DELETE_ROW':
      return state.filter((r) => r._localId !== action.localId);
    case 'DUPLICATE_ROW': {
      const idx = state.findIndex((r) => r._localId === action.localId);
      if (idx === -1) return state;
      const src = state[idx];
      const copy: EditorRow = { _localId: uuidv4(), task_name: src.task_name, description: src.description, extra: { ...src.extra }, due_date: src.due_date, assigned_to: src.assigned_to, group: src.group };
      return [...state.slice(0, idx + 1), copy, ...state.slice(idx + 1)];
    }
    case 'SET_ROW_ERRORS':
      return state.map((r) => r._localId === action.localId ? { ...r, _errors: action.errors } : r);
    case 'CLEAR_ERRORS':
      return state.map((r) => ({ ...r, _errors: {} }));
    case 'RESET':
      return [blankRow(), blankRow(), blankRow()];
    case 'DELETE_EXTRA_COL': {
      return state.map((r) => {
        const extra = { ...r.extra };
        delete extra[action.colKey];
        return { ...r, extra };
      });
    }
    case 'LOAD_ROWS':
      return action.rows.map((r) => ({
        _localId: uuidv4(),
        task_name: r.task_name,
        description: r.description ?? '',
        extra: {},
        due_date: r.due_date,
        assigned_to: r.assigned_to,
        group: r.tags?.find((t) => t.key === 'group')?.value ?? '',
      }));
    default:
      return state;
  }
}

function resolveAssignedTo(raw: string, teamMembers: TeamMember[]): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  const mention = trimmed.replace(/^@/, '').toLowerCase();
  const digits = trimmed.replace(/\D/g, '');
  for (const m of teamMembers) {
    const phone = `${m.country_code}${m.phone_number}`;
    const phoneDigits = phone.replace(/\D/g, '');
    if (digits && (phoneDigits === digits || phoneDigits.endsWith(digits))) return phone;
    if (mention === m.name.toLowerCase() || mention === m.name.split(' ')[0].toLowerCase()) return phone;
  }
  return trimmed;
}

// ─── Fill Handle ──────────────────────────────────────────────────────────────

function FillHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <Box
      className="fill-handle"
      position="absolute"
      bottom="-4px"
      right="-4px"
      w="8px"
      h="8px"
      bg="brand.500"
      border="1.5px solid white"
      borderRadius="1px"
      cursor="crosshair"
      zIndex={2}
      onMouseDown={onMouseDown}
    />
  );
}

// ─── Cell components ───────────────────────────────────────────────────────────

function TextCell({ id, value, onChange, onKeyDown, error, onFillStart }: {
  id?: string; value: string; onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void; error?: string;
  onFillStart?: () => void;
}) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          data-testid={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

function DateCell({ id, value, onChange, onKeyDown, error, onFillStart }: {
  id?: string; value: string; onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void; error?: string;
  onFillStart?: () => void;
}) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          data-testid={id}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={(e) => { try { (e.target as HTMLInputElement).showPicker?.(); } catch {} }}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

function AssignedToCell({ id, value, onChange, onKeyDown, error, teamMembers, onFillStart }: {
  id?: string; value: string; onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  error?: string; teamMembers: TeamMember[];
  onFillStart?: () => void;
}) {
  if (teamMembers.length === 0) {
    return <TextCell id={id} value={value} onChange={onChange} onKeyDown={onKeyDown} error={error} onFillStart={onFillStart} />;
  }
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Select
          id={id}
          data-testid={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        >
          <option value="">— select —</option>
          {teamMembers.map((m) => {
            const phone = `${m.country_code}${m.phone_number}`;
            const mention = `@${m.name.split(' ')[0].toLowerCase()}`;
            return <option key={m.id} value={phone}>{m.name} ({mention})</option>;
          })}
        </Select>
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateRows(rows: EditorRow[]): Map<string, Record<string, string>> {
  const errorMap = new Map<string, Record<string, string>>();
  for (const row of rows) {
    if (!row.task_name.trim() && !row.due_date && !row.assigned_to) continue; // skip blank rows
    const errors: Record<string, string> = {};
    if (!row.task_name.trim()) errors['task_name'] = 'Task name is required';
    if (!row.due_date) errors['due_date'] = 'Due date is required';
    if (!row.assigned_to.trim()) errors['assigned_to'] = 'Assigned to is required';
    if (Object.keys(errors).length > 0) errorMap.set(row._localId, errors);
  }
  return errorMap;
}

// ─── EventTaskTableEditor ─────────────────────────────────────────────────────

export interface EventTaskTableEditorProps {
  teamMembers: TeamMember[];
  onValidRows: (rows: UserBotTaskInput[]) => void;
  onValidationError: (count: number) => void;
  validateTriggerId?: string;
  importedRows?: UserBotTaskInput[];
  onDownloadTemplate?: () => void;
  onImport?: (file: File) => void;
  isImporting?: boolean;
}

export function EventTaskTableEditor({ teamMembers, onValidRows, onValidationError, validateTriggerId = 'event-task-editor-validate', importedRows, onDownloadTemplate, onImport, isImporting }: EventTaskTableEditorProps) {
  const [rows, dispatch] = useReducer(reducer, [], () => [blankRow(), blankRow(), blankRow()]);
  const [extraCols, setExtraCols] = useState<string[]>([]);
  const [addCount, setAddCount] = useState(5);
  const [focusedCell, setFocusedCell] = useState<{ rowIdx: number; col: string } | null>(null);
  const [fillState, setFillState] = useState<FillState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (importedRows && importedRows.length > 0) {
      const resolvedRows = importedRows.map((r) => ({ ...r, assigned_to: resolveAssignedTo(r.assigned_to, teamMembers) }));
      dispatch({ type: 'LOAD_ROWS', rows: resolvedRows });
    }
  }, [importedRows, teamMembers]);

  useEffect(() => {
    if (!focusedCell) return;
    const el = document.getElementById(`cell-${focusedCell.rowIdx}-${focusedCell.col}`);
    if (!el) return;
    el.focus();
    if (focusedCell.col === 'assigned_to') {
      setTimeout(() => (el as HTMLElement).click(), 0);
    }
  }, [focusedCell]);

  // Prevent text selection while drag-filling
  useEffect(() => {
    document.body.style.userSelect = fillState ? 'none' : '';
    return () => { document.body.style.userSelect = ''; };
  }, [!!fillState]);

  // Commit fill on mouseup anywhere in the document
  useEffect(() => {
    if (!fillState) return;
    const handleMouseUp = () => {
      const { srcRowIdx, col, value, endRow } = fillState;
      if (endRow !== srcRowIdx) {
        const lo = Math.min(srcRowIdx, endRow);
        const hi = Math.max(srcRowIdx, endRow);
        for (let i = lo; i <= hi; i++) {
          if (i === srcRowIdx) continue;
          dispatch({ type: 'SET_CELL', localId: rows[i]._localId, col, value });
        }
      }
      setFillState(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [fillState, rows]);

  const startFill = useCallback((rowIdx: number, col: string, value: string) => {
    setFillState({ srcRowIdx: rowIdx, col, value, endRow: rowIdx });
  }, []);

  const updateFillEndRow = useCallback((rowIdx: number) => {
    setFillState((prev) => (prev ? { ...prev, endRow: rowIdx } : null));
  }, []);

  const isFillTarget = useCallback((rowIdx: number, col: string): boolean => {
    if (!fillState || fillState.col !== col || rowIdx === fillState.srcRowIdx) return false;
    const lo = Math.min(fillState.srcRowIdx, fillState.endRow);
    const hi = Math.max(fillState.srcRowIdx, fillState.endRow);
    return rowIdx >= lo && rowIdx <= hi;
  }, [fillState]);

  const fillTargetShadow = 'inset 0 0 0 2px var(--chakra-colors-brand-500)';

  const fixedCols = useMemo(
    () => ['task_name', 'description', ...extraCols, 'due_date', 'assigned_to', 'group'],
    [extraCols]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIdx: number, col: string) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const colIdx = fixedCols.indexOf(col);
      if (e.shiftKey) {
        if (colIdx > 0) setFocusedCell({ rowIdx, col: fixedCols[colIdx - 1] });
        else if (rowIdx > 0) setFocusedCell({ rowIdx: rowIdx - 1, col: fixedCols[fixedCols.length - 1] });
      } else {
        if (colIdx < fixedCols.length - 1) setFocusedCell({ rowIdx, col: fixedCols[colIdx + 1] });
        else if (rowIdx < rows.length - 1) setFocusedCell({ rowIdx: rowIdx + 1, col: fixedCols[0] });
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) setFocusedCell({ rowIdx: rowIdx + 1, col });
    } else if (e.key === 'Escape') {
      setFocusedCell(null);
    }
  }, [fixedCols, rows.length]);

  const addExtraCol = () => {
    const key = `col_${extraCols.length + 1}`;
    setExtraCols((prev) => [...prev, key]);
  };

  const renameExtraCol = (oldKey: string, newKey: string) => {
    if (!newKey.trim() || newKey === oldKey) return;
    setExtraCols((prev) => prev.map((k) => (k === oldKey ? newKey.trim() : k)));
    rows.forEach((r) => {
      if (r.extra[oldKey] !== undefined) {
        dispatch({ type: 'SET_CELL', localId: r._localId, col: newKey.trim(), value: r.extra[oldKey] });
      }
    });
    dispatch({ type: 'DELETE_EXTRA_COL', colKey: oldKey });
  };

  const deleteExtraCol = (key: string) => {
    setExtraCols((prev) => prev.filter((k) => k !== key));
    dispatch({ type: 'DELETE_EXTRA_COL', colKey: key });
  };

  const validate = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
    const filledRows = rows.filter((r) => r.task_name.trim() || r.due_date || r.assigned_to.trim());
    const errorMap = validateRows(filledRows);
    if (errorMap.size > 0) {
      for (const [localId, errors] of errorMap) {
        dispatch({ type: 'SET_ROW_ERRORS', localId, errors });
      }
      onValidationError(errorMap.size);
      return;
    }
    const taskInputs: UserBotTaskInput[] = filledRows
      .filter((r) => r.task_name.trim())
      .map((r) => {
        const descParts = [r.description, ...extraCols.map((k) => r.extra[k] ?? '')].filter(Boolean);
        return {
          task_name: r.task_name.trim(),
          description: descParts.join(' | ') || undefined,
          due_date: r.due_date,
          assigned_to: r.assigned_to.trim(),
          tags: r.group.trim() ? [{ key: 'group', value: r.group.trim() }] : [],
        };
      });
    onValidRows(taskInputs);
  }, [rows, extraCols, onValidRows, onValidationError]);

  useEffect(() => {
    const el = document.getElementById(validateTriggerId);
    if (!el) return;
    const handler = () => validate();
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [validate, validateTriggerId]);

  const filledCount = rows.filter((r) => r.task_name.trim()).length;
  const totalErrors = rows.reduce((sum, r) => sum + Object.values(r._errors ?? {}).filter(Boolean).length, 0);

  return (
    <Box>
      <HStack justify="space-between" mb={2} spacing={2}>
        <HStack spacing={2}>
          {onDownloadTemplate && (
            <Button
              size="sm"
              variant="solid"
              colorScheme="gray"
              leftIcon={<MdDownload />}
              onClick={onDownloadTemplate}
              data-testid="ev-editor-download-template-btn"
            >
              Download Template
            </Button>
          )}
          {onImport && (
            <>
              <Button
                size="sm"
                variant="solid"
                colorScheme="gray"
                leftIcon={<FiUpload />}
                onClick={() => fileInputRef.current?.click()}
                isLoading={isImporting}
                loadingText="Parsing..."
                data-testid="ev-editor-import-btn"
              >
                Import
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onImport(file);
                  e.target.value = '';
                }}
              />
            </>
          )}
        </HStack>
        <HStack spacing={2}>
          <HStack spacing={1}>
            <Input
              type="number"
              value={addCount}
              onChange={(e) => setAddCount(Math.max(1, Math.min(100, Number(e.target.value))))}
              size="sm"
              w="60px"
              bg="bg-surface"
              borderColor="border-default"
              textAlign="center"
              data-testid="ev-editor-add-count-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => dispatch({ type: 'ADD_ROWS', count: addCount })}
              data-testid="ev-editor-add-rows-btn"
            >
              Add {addCount} row{addCount !== 1 ? 's' : ''}
            </Button>
          </HStack>
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            variant="outline"
            onClick={() => dispatch({ type: 'ADD_ROWS', count: 1 })}
            data-testid="ev-editor-add-row-btn"
          >
            Add row
          </Button>
        </HStack>
      </HStack>

      <Box overflowX="auto" borderWidth="1px" borderColor="border-default" borderRadius="md">
        <Table variant="simple" size="sm" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '700px' }}>
          <Thead bg="bg-muted" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <Tr>
              <Th w="36px" px={2} color="text-secondary" borderColor="border-default">#</Th>
              <Th minW="160px" px={0} color="text-primary" borderColor="border-default">
                Task Name <Text as="span" color="red.400">*</Text>
              </Th>
              <Th minW="140px" px={0} color="text-primary" borderColor="border-default">Description</Th>
              {extraCols.map((key) => (
                <Th key={key} minW="120px" px={0} color="text-primary" borderColor="border-default">
                  <HStack spacing={1}>
                    <Input
                      defaultValue={key}
                      placeholder="Column name"
                      size="xs"
                      variant="unstyled"
                      fontWeight="semibold"
                      color="text-primary"
                      px={1}
                      borderRadius="sm"
                      _hover={{ bg: 'bg-surface', boxShadow: '0 0 0 1px var(--chakra-colors-border-default)' }}
                      _focus={{ bg: 'bg-surface', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)', outline: 'none' }}
                      onBlur={(e) => renameExtraCol(key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    />
                    <IconButton
                      aria-label="Remove column"
                      icon={<FiX />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => deleteExtraCol(key)}
                    />
                  </HStack>
                </Th>
              ))}
              <Th w="36px" px={1} borderColor="border-default">
                <Tooltip label="Add description column">
                  <IconButton
                    aria-label="Add column"
                    icon={<FiPlus />}
                    size="xs"
                    variant="solid"
                    colorScheme="brand"
                    onClick={addExtraCol}
                  />
                </Tooltip>
              </Th>
              <Th minW="140px" px={0} color="text-primary" borderColor="border-default">
                Due Date <Text as="span" color="red.400">*</Text>
              </Th>
              <Th minW="160px" px={0} color="text-primary" borderColor="border-default">
                Assigned To <Text as="span" color="red.400">*</Text>
              </Th>
              <Th minW="120px" px={0} color="text-primary" borderColor="border-default">Group</Th>
              <Th w="68px" px={1} borderColor="border-default" />
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, rowIdx) => {
              const hasError = Object.values(row._errors ?? {}).some(Boolean);
              return (
                <Tr
                  key={row._localId}
                  borderLeftWidth={hasError ? '3px' : '0'}
                  borderLeftColor={hasError ? 'red.400' : 'transparent'}
                  _hover={{ bg: 'interactive-hover' }}
                  onMouseEnter={() => { if (fillState) updateFillEndRow(rowIdx); }}
                >
                  <Td px={2} borderColor="border-default" color="text-secondary" fontSize="xs" w="36px">
                    {rowIdx + 1}
                  </Td>
                  <Td p={0} borderColor="border-default" minW="160px" boxShadow={isFillTarget(rowIdx, 'task_name') ? fillTargetShadow : undefined}>
                    <TextCell
                      id={`cell-${rowIdx}-task_name`}
                      value={row.task_name}
                      onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'task_name', value: v })}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 'task_name')}
                      error={row._errors?.['task_name']}
                      onFillStart={() => startFill(rowIdx, 'task_name', row.task_name)}
                    />
                  </Td>
                  <Td p={0} borderColor="border-default" minW="140px" boxShadow={isFillTarget(rowIdx, 'description') ? fillTargetShadow : undefined}>
                    <TextCell
                      id={`cell-${rowIdx}-description`}
                      value={row.description}
                      onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'description', value: v })}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 'description')}
                      onFillStart={() => startFill(rowIdx, 'description', row.description)}
                    />
                  </Td>
                  {extraCols.map((key) => (
                    <Td key={key} p={0} borderColor="border-default" minW="120px" boxShadow={isFillTarget(rowIdx, key) ? fillTargetShadow : undefined}>
                      <TextCell
                        id={`cell-${rowIdx}-${key}`}
                        value={row.extra[key] ?? ''}
                        onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: key, value: v })}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, key)}
                        onFillStart={() => startFill(rowIdx, key, row.extra[key] ?? '')}
                      />
                    </Td>
                  ))}
                  <Td w="36px" px={1} borderColor="border-default" />
                  <Td p={0} borderColor="border-default" minW="140px" boxShadow={isFillTarget(rowIdx, 'due_date') ? fillTargetShadow : undefined}>
                    <DateCell
                      id={`cell-${rowIdx}-due_date`}
                      value={row.due_date}
                      onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'due_date', value: v })}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 'due_date')}
                      error={row._errors?.['due_date']}
                      onFillStart={() => startFill(rowIdx, 'due_date', row.due_date)}
                    />
                  </Td>
                  <Td p={0} borderColor="border-default" minW="160px" boxShadow={isFillTarget(rowIdx, 'assigned_to') ? fillTargetShadow : undefined}>
                    <AssignedToCell
                      id={`cell-${rowIdx}-assigned_to`}
                      value={row.assigned_to}
                      onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'assigned_to', value: v })}
                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 'assigned_to')}
                      error={row._errors?.['assigned_to']}
                      teamMembers={teamMembers}
                      onFillStart={() => startFill(rowIdx, 'assigned_to', row.assigned_to)}
                    />
                  </Td>
                  <Td p={0} borderColor="border-default" minW="120px" boxShadow={isFillTarget(rowIdx, 'group') ? fillTargetShadow : undefined}>
                    <Box
                      position="relative"
                      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
                    >
                      <TextCell
                        id={`cell-${rowIdx}-group`}
                        value={row.group}
                        onChange={(v) => dispatch({ type: 'SET_CELL', localId: row._localId, col: 'group', value: v })}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 'group')}
                        onFillStart={() => startFill(rowIdx, 'group', row.group)}
                      />
                    </Box>
                  </Td>
                  <Td px={1} borderColor="border-default" w="68px">
                    <HStack spacing={0}>
                      <IconButton
                        aria-label="Duplicate row"
                        icon={<FiCopy />}
                        size="xs"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={() => dispatch({ type: 'DUPLICATE_ROW', localId: row._localId })}
                        data-testid={`ev-editor-row-duplicate-${rowIdx}`}
                      />
                      <IconButton
                        aria-label="Delete row"
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => dispatch({ type: 'DELETE_ROW', localId: row._localId })}
                        data-testid={`ev-editor-row-delete-${rowIdx}`}
                      />
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={7 + extraCols.length} textAlign="center" py={8} color="text-secondary">
                  No rows — click "Add row" above.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <VStack align="flex-start" spacing={0} mt={2}>
        {totalErrors > 0 && (
          <Text fontSize="sm" color="red.500">
            {totalErrors} validation error{totalErrors !== 1 ? 's' : ''} — fix highlighted rows before saving.
          </Text>
        )}
        <Text fontSize="xs" color="text-secondary">
          Tip: Tab to move between cells · Enter to move down · Drag the blue corner handle to fill a value down · Click + to add description columns
          {filledCount > 0 && ` · ${filledCount} task${filledCount !== 1 ? 's' : ''} ready`}
        </Text>
      </VStack>

      <button id={validateTriggerId} style={{ display: 'none' }} />
    </Box>
  );
}
